import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { useLanguage } from '../../contexts/LanguageContext'

const { FiX } = FiIcons

const TAG_COLORS = {
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', hover: 'hover:bg-indigo-200' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', hover: 'hover:bg-purple-200' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', hover: 'hover:bg-teal-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', hover: 'hover:bg-blue-200' },
  green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', hover: 'hover:bg-green-200' },
  red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', hover: 'hover:bg-red-200' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', hover: 'hover:bg-orange-200' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300', hover: 'hover:bg-pink-200' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', hover: 'hover:bg-yellow-200' }
}

const TagInput = ({ value, onChange, placeholder, color = 'indigo', helpText, showOperationHint = false }) => {
  const { t } = useLanguage()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef(null)
  
  const tags = value ? value.split(',').map(t => t.trim()).filter(t => t) : []
  const colorTheme = TAG_COLORS[color] || TAG_COLORS.indigo

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag()
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      removeTag(tags.length - 1)
    }
  }

  const addTag = () => {
    const newTag = inputValue.trim()
    if (newTag && !tags.includes(newTag)) {
      const updatedTags = [...tags, newTag]
      onChange(updatedTags.join(', '))
      setInputValue('')
    }
  }

  const removeTag = (index) => {
    const updatedTags = tags.filter((_, i) => i !== index)
    onChange(updatedTags.join(', '))
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const newTags = pastedText.split(/[,\n]/).map(t => t.trim()).filter(t => t)
    const uniqueTags = [...new Set([...tags, ...newTags])]
    onChange(uniqueTags.join(', '))
    setInputValue('')
  }

  return (
    <div className="space-y-1.5">
      <div className={`min-h-[48px] p-2 border-2 ${colorTheme.border} rounded-lg bg-white focus-within:ring-2 focus-within:ring-${color}-500 focus-within:border-${color}-500 transition-all`}>
        <div className="flex flex-wrap gap-2">
          <AnimatePresence>
            {tags.map((tag, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-full border ${colorTheme.bg} ${colorTheme.text} ${colorTheme.border} ${colorTheme.hover} transition-colors`}
              >
                <span className="text-sm font-medium">{tag}</span>
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="ml-1 hover:bg-white/50 rounded-full p-0.5 transition-colors"
                >
                  <SafeIcon icon={FiX} className="h-3 w-3" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={addTag}
            placeholder={tags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] px-2 py-1 border-none focus:outline-none focus:ring-0 text-sm"
          />
        </div>
      </div>
      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}
      {showOperationHint && (
        <p className="text-xs text-gray-400 italic">💡 {t('petForm.tagOperationHint')}</p>
      )}
    </div>
  )
}

export default TagInput